import React, { useCallback, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';

import TipContext from '@home/components/TipContext';
import { downloadExcel } from '@/pages/dashboard/utils/download/index';
import type { IColumnFormatConfig } from '@/pages/dashboard/utils/download/_type';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';

/** 展示用 formatRule → Excel 数字格式（下载工具只认 Excel 格式串，不能把 thousands2Decimal 原样写入） */
const FORMAT_RULE_TO_EXCEL: Record<string, string> = {
  thousands2Decimal: '#,##0.00',
  twoDecimalBmk00: '#,##0.00',
  thousandsInit: '#,##0',
  intBmk00: '#,##0',
  percent2Decimal: '0.00%',
};

/** 与 pageConfig 中 CommonTable componentMetrics 运行时结构一致 */
export interface IStudioTableMetricForDownload {
  metricKey: string;
  enName?: string;
  cnName?: string;
  enWidth?: number;
  cnWidth?: number;
  formatRule?: string;
  downLoadFormatRule?: string | number;
  features?: {
    downLoadFormat?: string;
    cell?: {
      changeKey?: string;
      dodDownloadFormat?: string;
    };
  };
}

export function buildStudioTableDownloadColumns(
  metrics: IStudioTableMetricForDownload[],
  isEN: boolean,
): IColumnFormatConfig[] {
  const cols: IColumnFormatConfig[] = [];
  metrics.forEach(metric => {
    const name =
      getName(isEN, metric.enName, metric.cnName) || metric.metricKey || '';
    const fromRule =
      metric.formatRule && FORMAT_RULE_TO_EXCEL[metric.formatRule]
        ? FORMAT_RULE_TO_EXCEL[metric.formatRule]
        : undefined;
    const dlFmt =
      metric.features?.downLoadFormat ??
      (metric.downLoadFormatRule as string | undefined) ??
      fromRule ??
      '@';
    cols.push({
      code: metric.metricKey,
      name,
      format: dlFmt || '@',
      width: (isEN ? metric.enWidth : metric.cnWidth) || 120,
    });
    const dodKey = metric.features?.cell?.changeKey as string | undefined;
    if (dodKey) {
      const dodFmt = metric.features?.cell?.dodDownloadFormat || '0.00%';
      cols.push({
        code: dodKey,
        name: `${name} DoD`,
        format: dodFmt,
        width: 100,
      });
    }
  });
  return cols;
}

function pickMetricsFromRenderChild(
  child: React.ReactElement,
): IStudioTableMetricForDownload[] {
  const cfg = child.props?.config as
    | {
        props?: {
          commonBaseTableProps?: {
            componentMetrics?: IStudioTableMetricForDownload[];
          };
        };
      }
    | undefined;
  return cfg?.props?.commonBaseTableProps?.componentMetrics ?? [];
}

function mergeDownloadOperationIntoConfig(
  config: unknown,
  operation: React.ReactNode,
): unknown {
  if (!config || typeof config !== 'object') {
    return config;
  }
  const c = config as {
    props?: {
      metricsCardWrapProps?: Record<string, unknown>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  const baseProps = c.props && typeof c.props === 'object' ? c.props : {};
  const wrap =
    (baseProps.metricsCardWrapProps as Record<string, unknown>) || {};
  return {
    ...c,
    props: {
      ...baseProps,
      metricsCardWrapProps: {
        ...wrap,
        operation,
      },
    },
  };
}

export interface IStudioTableDownloadShellProps {
  dataKey: string;
  titleEn: string;
  titleCn: string;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  /** Service 注入 */
  data?: Record<string, unknown>;
  filterLoading?: boolean;
  children?: React.ReactNode;
}

/**
 * 放在 Service 与 Render(CommonTable) 之间：拿到合并后的 data[dataKey].data，
 * 向 metricsCardWrapProps 注入与 Performance Data 一致的下载按钮。
 */
const StudioTableDownloadShell: React.FC<IStudioTableDownloadShellProps> = ({
  dataKey,
  titleEn,
  titleCn,
  downloadFileNameEn,
  downloadFileNameCn,
  data,
  filterLoading,
  children,
}) => {
  const isEN = getLocale() === LANGUAGE_EN;
  const { formatMessage } = useIntl();

  const metrics = useMemo(() => {
    let list: IStudioTableMetricForDownload[] = [];
    React.Children.forEach(children, ch => {
      if (React.isValidElement(ch)) {
        list = pickMetricsFromRenderChild(ch);
      }
    });
    return list;
  }, [children]);

  const handleDownload = useCallback(async () => {
    const payload = data?.[dataKey] as { data?: unknown[] } | undefined;
    const allData = payload?.data;
    if (!Array.isArray(allData) || !allData.length) {
      return;
    }
    try {
      const fileName =
        getName(isEN, downloadFileNameEn, downloadFileNameCn) ||
        getName(isEN, titleEn, titleCn) ||
        'Download';
      await downloadExcel({
        fileName,
        tabs: [
          {
            tabName: getName(isEN, titleEn, titleCn) || 'Data',
            dataSource: allData,
            columns: buildStudioTableDownloadColumns(metrics, isEN),
          },
        ],
      });
    } catch (error) {
      console.error('StudioTableDownloadShell download failed:', error);
    }
  }, [
    data,
    dataKey,
    isEN,
    metrics,
    downloadFileNameEn,
    downloadFileNameCn,
    titleEn,
    titleCn,
  ]);

  const operationNode = useMemo(
    () => (
      <div className="dbd-common-table__operation-btn">
        <TipContext
          tipText={formatMessage({ id: 'dbl_common_download' })}
          type={{ default: 'download', hover: 'download' }}
          onClick={handleDownload}
        />
      </div>
    ),
    [handleDownload, formatMessage],
  );

  return (
    <>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        const mergedConfig = mergeDownloadOperationIntoConfig(
          child.props.config,
          operationNode,
        );
        return React.cloneElement(child, {
          data,
          filterLoading,
          config: mergedConfig,
        } as never);
      })}
    </>
  );
};

export default StudioTableDownloadShell;
